/* eslint-disable max-lines */
import {
  Component, OnInit, Input, ViewEncapsulation, OnChanges,
  AfterViewInit, OnDestroy, SimpleChanges, Inject, AfterViewChecked, ChangeDetectorRef
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, Validators, UntypedFormControl, UntypedFormBuilder } from '@angular/forms';
import { Observable, zip, of, iif } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { environment } from '@environment';

import { isNullOrUndefined } from '@app/util/util';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { BookingBaseContainer } from '@booking/containers/booking-base.container';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { BookingFacade } from '@booking/booking.facade';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { BOOKING_FORM_INPUTS } from '@booking/constants/forms.constants';
import { BookingArrangementVM, SubmitBookingArrangement } from '@booking/entities/booking-arrangement';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { TravelerInfoType, TravelerTypes } from '@pax/entities/traveler-info';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { BillingFacade } from '@billing/billing.facade';
import { PaxFacade } from '@pax/pax.facade';
import { PaxFormService } from '@pax/services/pax-form.service';
import { hotelBookingPayloadMapper } from '@booking/mappers/hotel-booking.payload.mapper';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { TravelerItemUI } from '@pax/entities/pax-ui';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { totalNights } from '@ux/mappers/guest.mapper';
import { AppFacade } from '@app/app.facade';
import {
  informativeBookingRoute, informativePaxCelebrateBookingRoute
} from '@booking/constants/booking-routes.constants';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { DEFAULT_EMAIL_CONTROL, DEFAULT_PHONE_CONTROL } from '@forms/contact-form/constants/contact-form.constants';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { AvoService } from '@avo/services/avo.service';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { LoyaltyProgramResponse, MarriottEnrollment, MarriottEnrollmentResponse } from '@pax/entities/loyalty-program.response';
import { MarriottCreateFormUIResponse } from '@pax/entities/marriott-create-form';
import { assistanceSearchAmplitudePropsMapper } from '@app/modules/core/mappers/assistance-amplitude.mapper';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { isActiveBonvoyNumberByAdults } from '@pax/mappers/active-bonvoy-number.mapper';

@Component({
  selector: 'app-booking-hotels',
  templateUrl: './booking-hotels.container.html',
  styleUrls: ['../booking-base.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class BookingHotelsContainer extends BookingBaseContainer
  implements OnInit, OnChanges, AfterViewInit, OnDestroy, AfterViewChecked {

  @Input() hotelValidation: HotelValidationResponse;

  public bookingHotelsForm: UntypedFormGroup;
  public travelerType: TravelerInfoType = TravelerInfoType.GUEST;
  public policiesType: SearchBy = SearchBy.HOTELS;

  private _allChildrenAges: number[][] = [];

  constructor(
    private readonly fb: UntypedFormBuilder,
    protected router: Router,
    protected route: ActivatedRoute,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected facade: BookingFacade,
    protected viewportScroller: ViewportScroller,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(
      route, router, cdRef, appFacade, viewportScroller, facade, billingFacade, paxFacade, 
      paxFormService, scrollFormService, propertiesAmplitudeService, bookingAmplitudeService, triggerAmplitudeService,
      assistanceFacade, fullValidTravelersPipe, avoService, travelerListVmPipe
    );
    this.travelerBookingProvider = TravelBookingType.HOTELS;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['user'] && this.bookingHotelsForm) {
      this.setUserData();
    }
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.subscriptions.push(
        this.celebratePaxView$.pipe(
          distinctUntilChanged(),
          tap(celebratePaxView => {
            if (celebratePaxView) {
              this.router.navigate([{ outlets: { [informativeBookingRoute]: [informativePaxCelebrateBookingRoute] } }], {
                state: { celebratePaxView },
                relativeTo: this.route, skipLocationChange: true
              });
            }
          })
        ).subscribe()
      );
      this.createForm();
      this.isLoadingBookingDelay$ = this.isLoadingBookingDelaySubscription();
      this.listenFormChanges(this.bookingHotelsForm);
      this.allChildrenAges$.pipe(
        tap(allChildrenAges => {
          this._allChildrenAges = allChildrenAges;
        })
      ).subscribe().unsubscribe();
      this.configDefaultByInfoType(this.travelerBookingProvider, this.startDate);
      this.listenToAssistanceSearchCall(
        this.hotelValidation, this.hotelValidation?.Amount?.CurrencyCode, this.route.snapshot.paramMap
      )
      this.checkUserMarriottLoyaltyNumber();
      this.subscriptions.push(
        this.activeBonvoyNumber$.pipe(
          switchMap(activeBonvoyNumberByLoad => this.travelers$.pipe(
            distinctUntilChanged(),
            tap(travelers => {
              const activeBonvoyNumberByTravelers = isActiveBonvoyNumberByAdults(travelers);
              if (activeBonvoyNumberByLoad && !activeBonvoyNumberByTravelers) {
                this.paxFacade.resetActiveBonvoyNumber();
              }
            })
          ))
        ).subscribe()
      );
    }
  }

  ngAfterViewInit(): void {
    if (this.user && this.bookingHotelsForm) this.setUserData();
  }

  ngOnDestroy(): void {
    this.assistanceFacade.resetAssistanceSearchInfo();
    if (!isNullOrUndefined(this.isLoadingBookingDelay$)) {
      this.isLoadingBookingDelay$.unsubscribe();
    }
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngAfterViewChecked(): void {
    this.checkPaxSection();
  }

  get guestsPerRoomState$(): Observable<Room[]> {
    return this.facade.guestsPerRoomState$;
  }

  get marriottloyaltyNumber$(): Observable<LoyaltyProgramResponse> {
    return this.paxFacade.marriottloyaltyNumber$;
  }

  get marriottCreatedAccount$(): Observable<MarriottEnrollmentResponse> {
    return this.paxFacade.marriottCreatedAccount$;
  }

  get marriottCreateAccountShowStatus$(): Observable<MarriottCreateFormUIResponse> {
    return this.paxFacade.marriottCreateAccountShowStatus$;
  }

  get hasHotelValidation(): boolean {
    return !isEmpty(this.hotelValidation);
  }

  get nights(): number {
    return this.hotelValidation?.HotelReservation ?
      totalNights(this.hotelValidation?.HotelReservation.Checkout, this.hotelValidation?.HotelReservation.Checkin) : 0;
  }

  get startDate(): string {
    return this.hotelValidation?.HotelReservation?.Checkin;
  }

  get pricePerNights(): number {
    return this.hotelValidation?.Amount?.Total / this.nights;
  }

  get isLoadingMarriottCreateForm$(): Observable<boolean> {
    return this.paxFacade.isLoadingMarriottCreateAccount$;
  }

  get activeBonvoyNumber$(): Observable<boolean> {
    return this.paxFacade.activeBonvoyNumber$;
  }

  get hoursMicroStay(): number {
    return this.hotelValidation?.HotelReservation?.Rooms[0]?.PackHour;
  }

  private get allChildrenAges$(): Observable<number[][]> {
    return this.guestsPerRoomState$.pipe(
      map(rooms => [].concat(rooms.map(room => room.childrenAges)))
    );
  }

  public isRequiredHotelTraveler(index: number): boolean {
    return index === 0;
  }

  public childrenAge(roomIndex: number, indexTypeNumber: number, type: TravelerTypes): number {
    if (type === TravelerTypes.ADULTS) {
      return null;
    } else {
      if (this._allChildrenAges?.length && this._allChildrenAges?.[roomIndex]?.length) {
        const roomAges = this._allChildrenAges[roomIndex];
        const first = roomAges[indexTypeNumber];
        return first;
      } else return null;
    }
  }

  public showHotelDetail(): void {
    this.router.navigate([{
      outlets: {
        'hotel-detail': [this.bookingDetail]
      }
    }], {
      state: {
        isHotelDetail: true,
        hotelValidation: this.hotelValidation,
        capacity: this.capacityMicroStay
      },
      relativeTo: this.route, skipLocationChange: true
    });
  }

  public submitBookingOptions(isDisabled: boolean): void {
    const searchBy = this.hotelValidation?.HotelReservation?.MicroStay ? SearchBy.BYHOURS : SearchBy.HOTELS;
    zip(
      this.submitToBookServiceSubscription$(this.bookingHotelsForm.value, null),
      this.propertiesAmplitudeService.searchProperties$(searchBy).pipe(take(1)),
      this.assistanceFacade.assistanceSearchInfo$
    ).pipe(
      switchMap((([{ props, trigger }, searchProps, assistanceSearchInfo]) => (
        this.avoService.getEventTriggerProps$(
          AVO_EVENTS.VIEW_TRAVEL_ASSISTANCE_MODAL,
          assistanceSearchAmplitudePropsMapper(assistanceSearchInfo?.Results?.[0]),
          searchProps
        ).pipe(
          tap(eventProps => {
            if (this.isActivePaidAssistance ) {
              this.fetchAssistanceModal(this.bookingHotelsForm, eventProps, isDisabled);
            } else {
              this.submitByBookServiceAction({ props, trigger }, this.bookingHotelsForm, this.travelerBookingProvider, searchBy);
            }
          })
        )
      )))
    ).subscribe().unsubscribe();
  }

  public submitBookingOptionsByAssistance(
    arrangement: BookingArrangementVM, assistanceInfo: { optionId: number, searchId: string }
  ): void {
    const searchBy = this.hotelValidation?.HotelReservation?.MicroStay ? SearchBy.BYHOURS : SearchBy.HOTELS;
    this.submitToBookServiceSubscription$(arrangement, assistanceInfo).pipe(
      tap(({ props, trigger }) => this.submitByBookServiceAction({ props, trigger },
        this.bookingHotelsForm, this.travelerBookingProvider, searchBy))
    ).subscribe().unsubscribe();
  }

  private createForm(): void {
    this.bookingHotelsForm = new UntypedFormGroup({
      [BOOKING_FORM_INPUTS.TRAVELERS]: this.fb.array([]),
      [BOOKING_FORM_INPUTS.PHONE]: DEFAULT_PHONE_CONTROL,
      [BOOKING_FORM_INPUTS.EMAIL]: DEFAULT_EMAIL_CONTROL,
      [BOOKING_FORM_INPUTS.COMMENTS]: new UntypedFormControl('', [
        Validators.maxLength(500),
      ]),
      [BOOKING_FORM_INPUTS.TERMS]: new UntypedFormControl(null, [
        Validators.required,
        Validators.minLength(3)
      ]),
      [BOOKING_FORM_INPUTS.BILLING]: this.fb.group({})
    });
    if (this.individualTerms) {
      this.bookingHotelsForm.addControl(BOOKING_FORM_INPUTS.EXTRA_TERMS, new UntypedFormControl(null, [
        Validators.required, Validators.minLength(3)
      ]));
    }
  }

  public setShowBillingModule(value: boolean): void {
    this.showBillingModule = value;
    this.resetbillingForm(this.bookingHotelsForm);
  }

  public createMarriottBonvoyAccount(payload: MarriottEnrollment): void {
    this.paxFacade.createMarriottBonvoyAccount(payload);
  }

  public resetMarriottCreateAccountShowStatus(): void {
    this.paxFacade.resetMarriottCreateAccountShowStatus();
  }

  private submitToBookServiceSubscription$(
    arrangement: BookingArrangementVM, assistanceInfo: { optionId: number, searchId: string }
  ): Observable<SubmitBookingArrangement> {
    const searchBy = this.hotelValidation?.HotelReservation?.MicroStay ? SearchBy.BYHOURS : SearchBy.HOTELS;
    return zip(
      this.route.paramMap.pipe(take(1)),
      this.guestsPerRoomState$.pipe(take(1)),
      this.billingFacade.defaultBillingInfo$.pipe(take(1)),
      this.propertiesAmplitudeService.searchProperties$(searchBy).pipe(take(1)),
      this.bookingReady$.pipe(take(1)),
      this.guestListUI$.pipe(take(1), map(guestListUI =>
        [].concat(...guestListUI.map(room => room.roomGuests.map(guest => guest))) as TravelerItemUI[])),
      this.appFacade.documentAssets$.pipe(map(documentAssets => documentAssets.PAX))
    ).pipe(
      map(([params, rooms, defaultBillingInfo, searchProps, isReadyBook, guests, documents]) => ({
        params, defaultBillingInfo, searchProps, rooms, guests, documents,
        isReadyBook: !!this.bookingHotelsForm.valid && !!this.hasHotelValidation && !!isReadyBook
      })),
      switchMap(({ isReadyBook, searchProps, params, defaultBillingInfo, rooms, guests, documents }) => iif(
        () => !!isReadyBook,
        zip(
          of(
            hotelBookingPayloadMapper(
              this.bookingHotelsForm, this.hotelValidation.ValidateOptionId,
              arrangement, defaultBillingInfo, rooms, guests, documents, assistanceInfo,
              this.hotelValidation.HotelReservation.BookingSource, this.hotelValidation?.HotelReservation?.MicroStay
            )
          ),
          this.bookingAmplitudeService.hotelCheckoutTriggerAmplitudeEntity$(this.hotelValidation,
            params, searchProps).pipe(take(1))
        ),
        of([null, null])
      )),
      map(([props, trigger]) => ({ props, trigger }))
    );
  }

  private setUserData(): void {
    this.bookingHotelsForm.patchValue({
      [BOOKING_FORM_INPUTS.EMAIL]: this.user.email ? this.user.email : '',
      [BOOKING_FORM_INPUTS.PHONE]: this.user.phone ?
        `${this.user.country_code ? this.user.country_code.replace('+', '') :
          environment.countryInfo.phone_prefix.replace('+', '')
        }${this.user.phone}` : ''
    });
  }

  private checkUserMarriottLoyaltyNumber(): void {
    if (this.hotelValidation?.HotelReservation?.BookingSource === HotelProvider.MarriottBonvoy) {
      this.paxFacade.fetchMarriottLoyaltyNumber(this.user.id);
    }
  }

}
